import { eachDayOfInterval, endOfDay, endOfMonth, max as maxDate, parseISO, startOfMonth, subMinutes } from 'date-fns';
import { countBy, intersection, intersectionWith, mapValues, omit, pick } from 'lodash-es';
import cn from 'classnames';
import {
  BaseViolationItem,
  ResourceShiftTotalsMap,
  SchedulerDayColumn,
  SchedulerResource,
  ScheduleStatusMode,
  ServiceRulesViolations,
  ShiftSiblingData,
} from '@lp/features/Scheduler/types';
import { selectedColumnHeaderClassname } from '@lp/features/Scheduler/constants';
import { ReservationNode, ReservationType } from '@/api/apis/resources';
import {
  AutoSchedulerInfo,
  AutoSchedulerResultType,
  BaseResource,
  BaseShift,
  MonthSchedule,
  PlanningProcess,
  PlanningProcessStatus,
  ResourcesWithShifts,
  ScheduleFillingState,
} from '@/api/apis/resources/schedule';
import { Entries } from '@/types/utils';
import { OrganType } from '@/api/apis/case';
import { getColorsByOrganType, getOrganTypeFromUnosId } from '@/utils/helpers';

export const getObjectEntries = <T extends object>(obj: T) => Object.entries(obj) as Entries<T>;

export const isDifferentHrSource = (shift: { isHrShift?: boolean }, prevShift?: { isHrShift?: boolean }): boolean =>
  shift.isHrShift !== prevShift?.isHrShift;

export const withoutTzOffset = (date: Date) => subMinutes(date, date.getTimezoneOffset());

export const toLocalDateFromApiDateKey = (apiDateKey: string) => parseISO(apiDateKey.split('T')[0]);

export const toZonedISODateKey = (date: Date) => {
  return withoutTzOffset(date)
    .toISOString()
    .replace(/\.\d+Z/gm, '+00:00');
};

export const parseTimeSpanToDurationString = (timeSpan: string) => {
  const [h, m] = timeSpan.split(':');
  return `${parseInt(h, 10)}h ${parseInt(m, 10)}m`;
};

export const getPlanningRefetchTime = (
  planningStatus?: PlanningProcessStatus | null,
  planningData?: PlanningProcess,
) => {
  const { status, jobCancellationRequested } = planningData ?? {};
  const isProcessing = [status, planningStatus].includes(PlanningProcessStatus.Processing);
  const isGenerating = status === PlanningProcessStatus.Generating;
  const isGenerationCancelling = isGenerating && !!jobCancellationRequested;

  switch (true) {
    case isProcessing:
    case isGenerationCancelling:
      return 1000;
    case isGenerating:
      return 10 * 1000;
    default:
      return undefined;
  }
};

export const getShiftSiblingsMap = <S extends BaseShift<string>>(
  dayCols: SchedulerDayColumn[],
  shifts: Record<string, S> | undefined,
  shiftSiblingGetter: (s: S, p?: S) => ShiftSiblingData,
) => {
  const siblingsMap: Map<string, ShiftSiblingData> = new Map();
  let prevKey: string;

  if (shifts) {
    dayCols.forEach(({ key }) => {
      const shift = shifts[key];

      if (shift) {
        const prevShift = shifts[prevKey];
        const prevSibling = siblingsMap.get(prevKey);
        const siblingData = shiftSiblingGetter(shift, prevShift);
        const { hasPrev, hasPartialPrev } = siblingData;
        const isSamePrev = hasPrev || hasPartialPrev;

        if (isSamePrev && prevSibling) {
          siblingsMap.set(prevKey, { ...prevSibling, hasNext: hasPrev, hasPartialNext: hasPartialPrev });
        }

        siblingsMap.set(key, siblingData);
      }

      prevKey = key;
    });
  }

  return siblingsMap;
};

const defaultGetRange = (date: Date) => ({
  start: startOfMonth(date),
  end: endOfMonth(date),
});

export const getResourceShiftTotalsMap = <
  R extends BaseResource,
  T extends string,
  O extends string,
  S extends BaseShift<T>,
>(
  selectedDay: Date,
  shiftsData: ResourcesWithShifts<R, S>,
  getter: ((shift?: S) => O | undefined) | keyof S,
  getRange = defaultGetRange,
) => {
  const resourceShiftTotalsMap: ResourceShiftTotalsMap<O> = {};
  const dates = eachDayOfInterval(getRange(selectedDay));
  const dayKeys = dates.map(toZonedISODateKey);

  Object.values(shiftsData).forEach((data) => {
    if (data.resource) {
      resourceShiftTotalsMap[data.resource.id] = countBy(pick(data.days, dayKeys), getter) as Record<O, number>;
    }
  });

  return resourceShiftTotalsMap;
};

export const getReservationsToDayMap = (reservations: ReservationNode[]) => {
  return reservations.reduce(
    (acc, reservation) => {
      const reservationDays = eachDayOfInterval({
        start: new Date(reservation.from),
        end: new Date(reservation.to ?? endOfDay(maxDate([reservation.from, new Date()]))),
      });
      reservationDays.forEach((d) => {
        const dayKey = toZonedISODateKey(d);
        acc[dayKey] = acc[dayKey] ?? [];
        acc[dayKey].push(reservation);
      });

      return acc;
    },
    {} as Record<string, ReservationNode[]>,
  );
};

export const getScheduleStatusMode = (monthSchedule?: MonthSchedule): ScheduleStatusMode | null => {
  if (!monthSchedule) return null;

  const isPublished = monthSchedule.hasSchedule && !!monthSchedule.scheduleId;
  if (isPublished) return ScheduleStatusMode.Published;

  const isPlannable = monthSchedule.hasPreSchedule && !monthSchedule.scheduleId;
  const withPlanning = monthSchedule.hasPlanningProcess && isPlannable;
  if (withPlanning) return ScheduleStatusMode.Planning;
  if (isPlannable) return ScheduleStatusMode.PrePlanning;

  return null;
};

export const getIsAutogenerated = (autoSchedulerInfo?: AutoSchedulerInfo | null) => {
  if (!autoSchedulerInfo) return false;
  return [AutoSchedulerResultType.Feasible, AutoSchedulerResultType.Optimal].includes(autoSchedulerInfo?.resultType);
};

export const getIsValidatable = (fillingState?: ScheduleFillingState) => {
  if (!fillingState) return false;
  return [ScheduleFillingState.Partial, ScheduleFillingState.Full].includes(fillingState);
};

export const getColumnHeaderClassname = (column: SchedulerDayColumn, bgColor = 'bg-base-70') => {
  const todayClassname = cn('text-base-20', column.isToday ? 'bg-base-50' : bgColor);
  return column.isSelectedDay ? selectedColumnHeaderClassname : todayClassname;
};

export const getReservationColors = (data: ReservationNode) => {
  const organType = data.caseName ? getOrganTypeFromUnosId(data.caseName) : null;
  const organColor = getColorsByOrganType(organType ?? undefined);

  switch (data.type) {
    case ReservationType.OnCase:
      return {
        text: organColor.textDefault,
        organText: organColor.textDefault,
      };
    case ReservationType.OnRest:
      return {
        text: organColor.textDefault,
        organText: organColor.textDefault,
      };
    case ReservationType.Reserved:
      return {
        text: 'text-warning-20',
        organText: organColor.textDefault,
      };
    default:
      return {
        text: 'text-base-25',
        organText: organColor.textDefault,
      };
  }
};

export const getScheduleDayViolations = (dayKey: string, violations?: ServiceRulesViolations) => {
  const daily = violations?.daily?.[dayKey];
  const weekly = violations?.weekly?.[dayKey];

  return daily && weekly ? [daily, weekly] : (daily ?? weekly);
};

export const getResourceEmptyShift = <R extends BaseResource, S extends BaseShift<string>>(
  resourceId: string,
  daysInterval: Date[],
  shiftsData: ResourcesWithShifts<R, S>,
) => {
  const dayKeys = daysInterval.map(toZonedISODateKey);
  const segment = shiftsData[resourceId];
  if (!segment) return null;

  for (const dayKey of dayKeys) {
    if (!segment.days[dayKey]) {
      return {
        day: toLocalDateFromApiDateKey(dayKey),
        resourceId,
      };
    }
  }

  return null;
};

export const getNumberInputValue = (value?: number) => (Number.isInteger(value) ? `${value}` : '');

export const getNumberInputValueHandler = (onChange: (...event: unknown[]) => void) => (value?: string) =>
  onChange(value === '' || value === undefined ? null : Number(value));

// TMDXLP-14091: Kidney is hidden from Schedule and the backend doesn't consume Kidney values
// for it — strip it from the outgoing payload instead of sending stale/placeholder values.
export const omitKidneyOrgan = <T>(organRecord: Partial<Record<OrganType, T>>) =>
  omit(organRecord, OrganType.Kidney) as Record<OrganType, T>;

export const omitKidneyOrganRules = <T>(perEntity: Record<string, Partial<Record<OrganType, T>>>) =>
  mapValues(perEntity, omitKidneyOrgan) as Record<string, Record<OrganType, T>>;

type ResourceFilters = {
  resources: string[];
  organs: OrganType[];
  phoneSupportOrgans: OrganType[];
  shadowOrgans: OrganType[];
  proctorOrgans: OrganType[];
};

export const getFilteredResources = <R extends SchedulerResource>(userList: R[], filters: ResourceFilters): R[] => {
  const { resources: resourcesFilter, organs: organsFilter, phoneSupportOrgans, shadowOrgans, proctorOrgans } = filters;

  const resourcesFilteredList =
    resourcesFilter.length > 0 ? intersectionWith(userList, resourcesFilter, (r, rId) => r?.id === rId) : userList;
  const organsFilteredList =
    organsFilter.length > 0
      ? resourcesFilteredList.filter((r) => intersection(organsFilter, r.organs).length > 0)
      : resourcesFilteredList;
  const phoneSupportFilteredList =
    phoneSupportOrgans.length > 0
      ? organsFilteredList.filter(
          (r) => r.canPhoneSupport && intersection(phoneSupportOrgans, r.phoneSupportOrgans).length > 0,
        )
      : organsFilteredList;
  const shadowOrganFilteredList =
    shadowOrgans.length > 0
      ? phoneSupportFilteredList.filter((r) => intersection(shadowOrgans, r.shadowOrgans).length > 0)
      : phoneSupportFilteredList;
  return proctorOrgans.length > 0
    ? shadowOrganFilteredList.filter((r) => intersection(proctorOrgans, r.proctorOrgans).length > 0)
    : shadowOrganFilteredList;
};

export const getIsHighlightedCell = <H extends { resourceId?: string; day?: Date }>(
  resourceId: string,
  date: Date,
  highlightValue?: H | null,
) => {
  if (!highlightValue?.resourceId && !highlightValue?.day) return false;

  const resourceMatch = !highlightValue.resourceId || highlightValue.resourceId === resourceId;
  const dayMatch = !highlightValue.day || highlightValue.day.toISOString() === date.toISOString();

  return resourceMatch && dayMatch;
};

export const resourceConflictHighlightGetter = <V extends BaseViolationItem>(item: V) => {
  return {
    resourceId: item.id,
  };
};

export const dayConflictHighlightGetter = <V extends BaseViolationItem>(item: V) => {
  return {
    day: item.day ? toLocalDateFromApiDateKey(item.day) : undefined,
  };
};
